import { CSSProperties, useMemo } from 'react';
import { Box, Button, ButtonProps, Link, Stack, styled, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import Markdown from 'markdown-to-jsx';
import { SpinWheel, SpinWheelSector, RisingSun } from '@ocx/spin-the-wheel';

import { PointsStrategy, RandomLoyaltySegment } from '../../../graphql/types.generated';
import { SizingMultiplier } from '../../../theme/constants';
import { messagesSpinToWin } from './messages.spin-to-win';
import { SpinToWinWheelSectorContent } from './SpinToWinWheelSectorContent';

const RISING_SUN_STYLES: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
};

const SpinToWinButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.SpinWheel.risingSunBackColor,
  backgroundColor: theme.palette.SpinWheel.risingSunTextColor,
  '&:hover': {
    backgroundColor: theme.palette.SpinWheel.risingSunTextColor,
  },
  '&:active': {
    backgroundColor: theme.palette.SpinWheel.risingSunTextColor,
  },
  '&:focus': {
    backgroundColor: theme.palette.SpinWheel.risingSunTextColor,
  },
}));

export type SpinToWinGameProps = {
  sectors: Array<RandomLoyaltySegment>;
  termsAndConditionsLink?: string;
  numberOfGamePlays?: number;
  onPlayClick?: () => void;
  onSpinningEnd?: (sectorIndex: number) => void;
  isSpinning?: boolean;
  winningSectorIndex?: number;
  pointsStrategy: PointsStrategy;
};

export const SpinToWinGame = ({
  sectors,
  termsAndConditionsLink,
  numberOfGamePlays,
  onPlayClick,
  onSpinningEnd,
  isSpinning,
  winningSectorIndex,
  pointsStrategy,
}: SpinToWinGameProps) => {
  const intl = useIntl();
  const { palette } = useTheme();

  const markdownOptions = useMemo(
    () => ({
      overrides: {
        a: {
          component: Link,
          props: {
            color: palette.SpinWheel.risingSunTextColor,
            underline: 'always',
            target: '_blank',
            rel: 'noreferrer',
          },
        },
      },
    }),
    [palette.SpinWheel.risingSunTextColor],
  );

  const items = useMemo<SpinWheelSector[]>(() => {
    return sectors.map((sector, index) => ({
      id: String(index),
    }));
  }, [sectors]);

  return (
    <>
      <RisingSun style={RISING_SUN_STYLES} />
      <Stack justifyContent="space-between" textAlign="center" minHeight="100%" sx={{ position: 'relative' }}>
        <Typography
          variant="h3"
          textTransform="uppercase"
          paddingTop="10vh"
          color={palette.SpinWheel.risingSunTextColor}>
          {intl.formatMessage(messagesSpinToWin['spinToWinGame:title'])}
        </Typography>

        <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translateX(-50%) translateY(-50%)' }}>
          <SpinWheel
            onSpinningEnd={onSpinningEnd}
            size="min(85vw, 85vh, 550px)"
            rimWidth={7}
            sectors={items}
            renderSectorContent={(_, index) => (
              <SpinToWinWheelSectorContent sector={sectors[index]} index={index} pointsStrategy={pointsStrategy} />
            )}
            isInfinitySpinning={isSpinning}
            sectorIndex={winningSectorIndex}
          />
        </Box>

        <Box padding={SizingMultiplier.lg} marginBottom="var(--ion-safe-area-bottom)">
          {termsAndConditionsLink && (
            <Typography
              variant="caption"
              display="inline-block"
              mt={SizingMultiplier.sm}
              px={SizingMultiplier.md}
              mb={SizingMultiplier.lg}
              borderRadius={SizingMultiplier.md}
              color={palette.SpinWheel.risingSunTextColor}
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
              }}>
              <Markdown options={markdownOptions}>
                {intl.formatMessage(messagesSpinToWin['spinToWinGame:terms-and-conditions'], {
                  termsAndConditionsLink,
                })}
              </Markdown>
            </Typography>
          )}
          <SpinToWinButton
            disabled={!onPlayClick}
            onClick={onPlayClick}
            variant="contained"
            size="large"
            disableElevation
            fullWidth>
            {intl.formatMessage(messagesSpinToWin['spinToWinGame:button'])}{' '}
            {numberOfGamePlays && numberOfGamePlays > 1 && (
              <Typography
                display="inline-flex"
                position="absolute"
                alignItems="center"
                px={SizingMultiplier.md}
                py={SizingMultiplier.xs}
                borderRadius={SizingMultiplier.lg}
                sx={{
                  color: ({ palette }) => palette.SpinWheel.risingSunTextColor,
                  backgroundColor: ({ palette }) => palette.SpinWheel.risingSunBackColor,
                  right: (theme) => theme.spacing(SizingMultiplier.sm),
                  opacity: !onPlayClick ? 0.5 : 1,
                  pointerEvents: 'none',
                }}>
                &times;{numberOfGamePlays}
              </Typography>
            )}
          </SpinToWinButton>
        </Box>
      </Stack>
    </>
  );
};
